import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { toNumberOrNull } from '@shared/utils';
import { useAddCategoryToSection, useCategory } from '../../hooks/useCategories';
import { useSections } from '@modules/sections';
import { Button, Modal, Select } from '@shared/ui';

interface AddToSectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    categoryId: number;
    onSuccess?: () => void;
}

export default function AddToSectionModal({
    isOpen,
    onClose,
    categoryId,
    onSuccess,
}: AddToSectionModalProps) {
    const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
    const addCategoryToSection = useAddCategoryToSection();
    const { data: sections = [] } = useSections();
    const { data: category } = useCategory(categoryId);

    // Получаем список ID секций, в которых категория уже есть
    // Структура: category.sections = [{ sectionId: number, section: {...} }]
    const existingSectionIds = (category as any)?.sections?.map((sc: any) => sc.sectionId) || [];

    const handleAddToSection = async () => {
        if (!selectedSectionId || !categoryId) {
            return;
        }

        // Проверяем, не добавлена ли уже категория в эту секцию
        if (existingSectionIds.includes(selectedSectionId)) {
            notifications.show({
                title: 'Ошибка',
                message: 'Категория уже добавлена в эту секцию',
                color: 'red',
            });
            return;
        }

        try {
            await addCategoryToSection.mutateAsync({
                id: categoryId,
                sectionId: selectedSectionId,
            });

            notifications.show({
                title: 'Успешно',
                message: 'Категория успешно добавлена в секцию',
                color: 'green',
            });

            // Закрываем модальное окно и сбрасываем выбор
            setSelectedSectionId(null);
            onClose();
            onSuccess?.();
        } catch (error) {
            console.error('Ошибка при добавлении категории в секцию:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при добавлении категории в секцию',
                color: 'red',
            });
        }
    };

    const handleClose = () => {
        setSelectedSectionId(null);
        onClose();
    };

    const isLoading = addCategoryToSection.isPending;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size={400}
            title="Добавить категорию в секцию"
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Select
                    placeholder="Выберите секцию"
                    value={selectedSectionId}
                    onChange={(value) => setSelectedSectionId(toNumberOrNull(value))}
                    data={sections.map(section => {
                        const isDisabled = existingSectionIds.includes(section.id);
                        return {
                            value: section.id.toString(),
                            label: isDisabled ? `${section.name} (уже добавлена)` : section.name,
                            disabled: isDisabled,
                        };
                    })}
                    style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button onClick={handleClose} variant="secondary">
                        Отмена
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddToSection}
                        disabled={!selectedSectionId || isLoading}
                        loading={isLoading}
                    >
                        Добавить
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

